import { Injectable } from "@angular/core";
import { of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { FormatDeployLabelService } from "../../format-deploy-label/format-deploy-label.service";
import { AttributeService } from "../../models/attribute.service";
import { DeployService } from "../../models/deploy.service";
import { PlanTierService } from "../../plan-tier/plan-tier.service";
import { StdAttrsService } from "../../std-attrs/std-attrs.service";

interface ISearchSuggestion {
  label: string;
  value: string;
}

@Injectable()
export class SearchDataService {
  constructor(
    private attributeService: AttributeService,
    private deployService: DeployService,
    private formatDeployLabel: FormatDeployLabelService,
    private planTierService: PlanTierService,
    private stdAttrsService: StdAttrsService,
  ) {}

  public loadData(projectId: number, customAttrs: any) {
    if (customAttrs == null) {
      customAttrs = [];
    }
    return this.planTierService.features().pipe(
      mergeMap((features: any) => {
        return this.doLoadData(projectId, customAttrs, features);
      }),
    );
  }

  private doLoadData(projectId: any, customAttrs: any, features: any) {
    const searchData: any = [];

    const addAttr = (attr: any) => {
      attr.featureDisabled = !this.planTierService.allowsSearchAttr(
        attr,
        features,
      );
      return searchData.push(attr);
    };

    const attrQuery = (attr: any, val: any) => {
      let params;
      params = {
        projectId,
        limit: 10,
        attribute: attr,
        search: val,
      };
      return this.attributeService.query(params);
    };

    const addCustomAttr = (attrKey: string) => {
      const valueKey = attrKey.split(".").pop();
      return addAttr({
        key: attrKey,
        name: attrKey,
        type: "text",
        custom: true,
        suggestionFormat(value: any) {
          return value[valueKey];
        },
        suggestions(searchTerm: string) {
          return attrQuery(attrKey, searchTerm).pipe(
            map((data: any) => {
              const attrData = data.attributes || [];
              return attrData.map((attr: any) => {
                return {
                  label: attr.value[valueKey],
                  value: attr.id,
                  groupCount: attr.groupCount,
                };
              });
            }),
          );
        },
      });
    };

    const addStandardAttr = (stdAttr: any) => {
      return addAttr({
        key: stdAttr.key,
        name: stdAttr.searchLabel,
        type: "text",
        placeholder: stdAttr.searchLabel.toLowerCase(),
        suggestionFormat: stdAttr.suggestionFormat,
        suggestions(searchTerm: string) {
          return attrQuery(stdAttr.key, searchTerm).pipe(
            map((data: any) => {
              const attrData = data.attributes || [];
              return attrData.map((attr: any) => {
                return {
                  label: stdAttr.suggestionFormat(attr.value),
                  value: attr.id,
                  groupCount: attr.groupCount,
                };
              });
            }),
          );
        },
      });
    };

    function filterSuggestions(
      suggestions: ISearchSuggestion[],
      searchTerm: string,
      property: keyof ISearchSuggestion,
    ) {
      if (searchTerm == null) {
        return suggestions;
      }

      return suggestions.filter((suggestion) => {
        return (
          suggestion[property].toLowerCase().indexOf(searchTerm.toLowerCase()) >
          -1
        );
      });
    }

    addAttr({
      key: "resolved",
      name: "Status",
      type: "text",
      placeholder: "resolved...",
      label: "unresolved",
      value: "false",
      remainOnRemoveAll: true,
      suggestions(searchTerm: string) {
        const items = [
          {
            label: "resolved",
            value: "true",
          },
          {
            label: "unresolved",
            value: "false",
          },
        ];
        return of(filterSuggestions(items, searchTerm, "value"));
      },
    });

    addAttr({
      key: "muted",
      name: "Muted",
      type: "text",
      placeholder: "true...",
      suggestions(searchTerm: string) {
        const items = [
          {
            label: "true",
            value: "true",
          },
          {
            label: "false",
            value: "false",
          },
        ];
        return of(filterSuggestions(items, searchTerm, "value"));
      },
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const datepickerMaxDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };

    addAttr({
      key: "end_time",
      name: "Before",
      type: "date",
      placeholder: "2016-10-22...",
      datepickerMaxDate,
    });

    addAttr({
      key: "start_time",
      name: "After",
      type: "date",
      placeholder: "2016-08-22...",
      datepickerMaxDate,
    });

    addAttr({
      key: "on",
      name: "On",
      type: "date",
      placeholder: "2016-08-22...",
      datepickerMaxDate,
    });

    for (const attrKey of customAttrs) {
      addCustomAttr(attrKey);
    }

    for (const attr of this.stdAttrsService.getAttrs()) {
      addStandardAttr(attr);
    }

    addAttr({
      key: "has_comments",
      name: "Has comments",
      type: "text",
      placeholder: "true...",
      suggestions(searchTerm: string) {
        const items = [
          {
            label: "true",
            value: "true",
          },
          {
            label: "false",
            value: "false",
          },
        ];
        return of(filterSuggestions(items, searchTerm, "value"));
      },
    });

    addAttr({
      key: "severity",
      name: "Severity",
      type: "text",
      placeholder: "error...",
      suggestions(searchTerm: string) {
        const items = [
          {
            label: "debug",
            value: "debug",
          },
          {
            label: "info",
            value: "info",
          },
          {
            label: "notice",
            value: "notice",
          },
          {
            label: "warning",
            value: "warning",
          },
          {
            label: "error",
            value: "error",
          },
          {
            label: "critical",
            value: "critical",
          },
          {
            label: "alert",
            value: "alert",
          },
          {
            label: "emergency",
            value: "emergency",
          },
        ];
        return of(filterSuggestions(items, searchTerm, "value"));
      },
    });

    return this.deployService
      .groupsQuery({
        projectId,
        limit: 100,
      })
      .pipe(
        map((data: any) => {
          let deploys = data.deploys || [];
          deploys = deploys.map((dep: any) => {
            return {
              label: this.formatDeployLabel.labelFor(dep),
              value: dep.id,
              groupCount: dep.groupCount,
            };
          });
          if (deploys.length) {
            addAttr({
              key: "deploy_id",
              name: "Deploy",
              type: "text",
              placeholder: "deploy...",
              suggestions(searchTerm: string) {
                return of(filterSuggestions(deploys, searchTerm, "label"));
              },
            });
            addAttr({
              key: "resolved_by_deploy_id",
              name: "Resolved by deploy",
              type: "text",
              placeholder: "deploy...",
              restrictToSuggestions: true,
              suggestions(searchTerm: string) {
                return of(filterSuggestions(deploys, searchTerm, "label"));
              },
            });
          }

          return searchData;
        }),
      );
  }
}
